import React, { useState } from "react";
import {
  Utensils,
  Phone,
  ChefHat,
  CheckCircle2,
  Circle,
  MessageSquare,
  Copy,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const FoodControlCard = () => {
  const [isExpanded, setIsExpanded] = useState(false); // Default to collapsed

  const [tasks, setTasks] = useState([
    {
      id: "mom_comm",
      label: "Call Mom: Confirm GF Plan (Conrad's)",
      status: "done",
      day: "Monday",
    },
    {
      id: "groceries",
      label: "Pickup: Shopping & Groceries",
      status: "todo",
      day: "Tuesday",
    },
    {
      id: "prep",
      label: "Kitchen: Prepare Food for the 24th",
      status: "todo",
      day: "Tuesday",
    },
  ]);
  const [showDraft, setShowDraft] = useState(false);

  const toggleTask = (id) =>
    setTasks(
      tasks.map((t) =>
        t.id === id
          ? { ...t, status: t.status === "done" ? "todo" : "done" }
          : t
      )
    );
  const draftText =
    "Morning Mom! 👋 Quick update on food for Christmas, so you have everything while shopping today:\n\n1. Melyssa & Emma are totally set—they decided on Gluten Free pizza & salad from Conrad's. We are handling. We also have Gluten-Free brownies prepared for dessert.\n2. Laura & I are sticking with the Chinese food plan. Everything you always have for the larger group is perfect for us.\n\nMeaning: Don’t worry about ANY gluten-free cooking or dinner. Anything that doesn’t have wheat/gluten/bread like vegetables they would eat as apps throughout the day. Love you!!";

  // Calculate high-level status for collapsed view
  const momStatus = tasks.find((t) => t.id === "mom_comm").status === "done";

  return (
    <div className="mb-6 border-l-4 border-emerald-500 bg-white rounded-r-xl shadow-sm overflow-hidden transition-all">
      {/* HEADER (CLICK TO TOGGLE) */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-5 border-b border-stone-100 bg-emerald-50/30 cursor-pointer hover:bg-emerald-50/50 transition-colors flex justify-between items-center"
      >
        <div>
          <div className="flex items-center gap-2 text-emerald-700 mb-1">
            <Utensils className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">
              Menus & Logistics
            </span>
          </div>
          <h2 className="text-lg font-bold text-stone-800">
            🥗 Holiday Food Execution
          </h2>
          {/* Collapsed Summary */}
          {!isExpanded && (
            <div className="mt-1 flex items-center gap-2 text-xs font-medium text-emerald-600">
              {momStatus ? (
                <CheckCircle2 className="w-3 h-3" />
              ) : (
                <Circle className="w-3 h-3" />
              )}
              {momStatus ? "Mom Briefed & Aligned" : "Action Needed: Call Mom"}
            </div>
          )}
        </div>

        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-stone-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-stone-400" />
        )}
      </div>

      {/* EXPANDABLE CONTENT */}
      {isExpanded && (
        <div className="p-5 space-y-6 animate-in slide-in-from-top-2 duration-200">
          {/* MONDAY CRITICAL */}
          <section className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 p-2 rounded-full text-amber-600 mt-1">
                <Phone className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-stone-800 mb-1">
                  Monday Mission: The "Conrad's" Call
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed mb-3">
                  Communicate strictly to Mom that she does <strong>not</strong>{" "}
                  need to cook/shop for the GF crew.
                </p>

                <div className="bg-white p-3 rounded border border-amber-200/50 text-xs space-y-2 mb-3">
                  <div className="font-bold text-stone-400 uppercase tracking-widest mb-1">
                    Dec 25th Menu Split
                  </div>
                  <div className="flex justify-between items-center border-b border-stone-100 pb-2">
                    <span className="font-semibold text-stone-700">
                      Ben & Laura
                    </span>
                    <span className="text-stone-500">🥡 Chinese Food</span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="font-semibold text-emerald-700">
                      Melyssa & Emma (GF)
                    </span>
                    <span className="text-emerald-700 font-medium">
                      🍕 Conrad's Pizza & Greek Salad
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setShowDraft(!showDraft)}
                  className="flex items-center gap-2 text-xs font-bold text-amber-600 uppercase tracking-wider mb-2 hover:text-amber-800 transition-colors"
                >
                  <MessageSquare className="w-3 h-3" />
                  {showDraft ? "Hide Sent Message" : "View Sent Message"}
                </button>

                {showDraft && (
                  <div className="bg-white p-3 rounded border border-amber-200 text-sm text-stone-600 italic mb-4 animate-in slide-in-from-top-2 relative">
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {draftText}
                    </div>
                    <div className="mt-2 pt-2 border-t border-stone-100 flex justify-end">
                      <span className="text-[10px] text-stone-400 uppercase font-bold flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Sent
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => toggleTask("mom_comm")}
                  className="flex items-center gap-2 mt-2 text-sm font-bold text-amber-700 hover:text-amber-800 transition-colors"
                >
                  {tasks.find((t) => t.id === "mom_comm").status === "done" ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <Circle className="w-5 h-5" />
                  )}
                  <span>Mom Briefed & Aligned</span>
                </button>
              </div>
            </div>
          </section>

          {/* EXECUTION */}
          <section>
            <div className="flex items-center gap-2 mb-3 text-stone-400">
              <ChefHat className="w-4 h-4" />
              <h3 className="text-xs font-bold uppercase tracking-widest">
                My Execution (Tue/Wed)
              </h3>
            </div>
            <div className="space-y-3">
              {[
                {
                  id: "groceries",
                  label: "Pick up Shopping & Groceries",
                  sub: "Target: Tuesday",
                },
                {
                  id: "prep",
                  label: "Prepare Food for Dec 24th",
                  sub: "Target: Tuesday Night / Wed Morning",
                },
              ].map((item) => (
                <div
                  key={item.id}
                  onClick={() => toggleTask(item.id)}
                  className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-100 cursor-pointer hover:bg-stone-100 transition-colors"
                >
                  <div
                    className={
                      tasks.find((t) => t.id === item.id).status === "done"
                        ? "text-emerald-600"
                        : "text-stone-300"
                    }
                  >
                    {tasks.find((t) => t.id === item.id).status === "done" ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-stone-700">
                      {item.label}
                    </div>
                    <div className="text-xs text-stone-400">{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default FoodControlCard;
